import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FilePlus2, FileText, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const NewDoc = ({ companies }) => {
  const nav = useNavigate();
  const [cid, setCid] = useState("");
  const [title, setTitle] = useState("Platform proposal");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data: m } = await api.get("/admin/proposals/modules");
      const modules = m.modules.filter((x) => !x.new).map(({ key, name, description }) => ({ key, name, description }));
      const { data } = await api.post("/admin/proposals", { company_id: cid, title, modules });
      nav(`/admin/proposals/${data.id}`);
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-4" data-testid="new-proposal-form">
      <div className="space-y-1"><div className="text-xs font-medium text-slate-500">Company</div>
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger className="h-9 w-56" data-testid="new-proposal-company"><SelectValue placeholder="Choose a company" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-1"><div className="text-xs font-medium text-slate-500">Title</div>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-9 w-72" data-testid="new-proposal-title" /></div>
      <Button onClick={create} disabled={!cid || title.trim().length < 2 || busy} className="gap-1.5 bg-teal-600 hover:bg-teal-700" data-testid="create-proposal-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />} Create document
      </Button>
    </div>
  );
};

export default function ProposalsPage() {
  const [docs, setDocs] = useState(null);
  const [companies, setCompanies] = useState([]);
  const load = () => api.get("/admin/proposals").then((r) => setDocs(r.data.documents)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); api.get("/admin/companies").then((r) => setCompanies(r.data.companies || r.data)).catch(() => {}); }, []);
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? People with access lose it immediately.`)) return;
    await api.delete(`/admin/proposals/${d.id}`).then(load).catch((e) => toast.error(apiError(e)));
  };
  return (
    <div className="space-y-5" data-testid="proposals-page">
      <PageHeader eyebrow="Platform" title="Proposal documents" subtitle="Generate a branded PDF of the platform's modules from a company's live screens, with amounts and names blurred." />
      <NewDoc companies={companies} />
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="proposals-list">
        {!docs && <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
        {docs?.length === 0 && <p className="px-4 py-10 text-center text-sm text-slate-500">No documents yet</p>}
        {docs?.map((d) => (
          <div key={d.id} className="flex items-center gap-3 border-t border-slate-100 px-4 py-3" data-testid={`proposal-row-${d.id}`}>
            <FileText className="h-5 w-5 text-teal-600" />
            <Link to={`/admin/proposals/${d.id}`} className="min-w-0 flex-1">
              <div className="truncate font-medium text-slate-900">{d.title}</div>
              <div className="text-xs text-slate-500">{d.company} · {d.modules.length} modules · {d.versions.length ? `v${d.versions.at(-1).v} · ${fmtDate(d.versions.at(-1).created_at)}` : "not generated yet"}{d.job.state === "running" && " · generating…"}</div>
            </Link>
            <Button variant="ghost" size="icon" onClick={() => remove(d)} data-testid={`delete-proposal-${d.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
